import { useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import {
    AlertTriangle, Info, TrendingUp, TrendingDown, Target, RefreshCw,
    Users, CalendarRange, Fish, Crown, ChevronDown, ChevronUp, Lightbulb,
} from 'lucide-react';
import { MetricCard } from '@/components/shared/MetricCard';
import {
    CATEGORY_LABELS,
    EXPERIENCE_LABELS,
    WATER_LABELS,
    BEHAVIOR_LABELS,
    type LandingPatterns,
    type ActionRetentionRow,
} from '@/hooks/useEarlyLandingAnalytics';
import {
    useEarlyLandingPlaybooks,
    PLAYBOOK_GOAL_LABELS,
    STAGE_ORDER,
    type LandingPlaybook,
    type PlaybookDriver,
    type PlaybookProfile,
} from '@/hooks/useEarlyLandingPlaybooks';
import {
    DEFAULT_PROFILE_LENS,
    describeLens,
    isLensActive,
    type ProfileLens,
} from '@/components/analytics/profileLens';
import { MainScreensPanel } from '@/components/analytics/MainScreensPanel';
import { formatLiftPp, liftSampleReady, MIN_LIFT_GROUP } from '@/components/analytics/sampleSize';

type MatrixSort = 'lift_session_5' | 'lift_day_7' | 'did_users' | 'label';
type FocusTab = 'summary' | 'retention' | 'captures' | 'pro' | 'profiles' | 'stages' | 'evidence';

interface EarlyLandingPatternsPanelProps {
    days: number;
    patterns: LandingPatterns;
    lens?: ProfileLens;
}

const GOAL_STYLE: Record<string, { border: string; bg: string; badge: string; icon: React.ReactNode }> = {
    retention: {
        border: 'border-primary/30',
        bg: 'bg-primary/5',
        badge: 'bg-primary/15 text-primary',
        icon: <Target size={14} className="text-primary" />,
    },
    captures: {
        border: 'border-emerald-500/30',
        bg: 'bg-emerald-500/5',
        badge: 'bg-emerald-500/15 text-emerald-400',
        icon: <Fish size={14} className="text-emerald-400" />,
    },
    pro: {
        border: 'border-amber-500/30',
        bg: 'bg-amber-500/5',
        badge: 'bg-amber-500/15 text-amber-400',
        icon: <Crown size={14} className="text-amber-400" />,
    },
};

function pct(v: number | null | undefined, fallback = '—') {
    if (v == null) return fallback;
    return `${v}%`;
}

export function EarlyLandingPatternsPanel({
    days,
    patterns,
    lens = DEFAULT_PROFILE_LENS,
}: EarlyLandingPatternsPanelProps) {
    const { data, isLoading, isError, error, refetch, isFetching } = useEarlyLandingPlaybooks(days, true);
    const lensActive = isLensActive(lens);
    const [focus, setFocus] = useState<FocusTab>(lensActive && lens.experience !== 'all' ? 'profiles' : 'summary');
    const [matrixSort, setMatrixSort] = useState<MatrixSort>('lift_session_5');
    const [matrixCategory, setMatrixCategory] = useState<string>('all');
    const [showEvidence, setShowEvidence] = useState(false);

    useEffect(() => {
        if (lens.experience !== 'all') setFocus('profiles');
    }, [lens.experience]);

    const categories = useMemo(() => {
        const set = new Set(patterns.action_retention_matrix.map((r) => r.category));
        return ['all', ...Array.from(set).sort()];
    }, [patterns.action_retention_matrix]);

    const sortedMatrix = useMemo(() => {
        const rows = patterns.action_retention_matrix.filter(
            (r) => matrixCategory === 'all' || r.category === matrixCategory
        );
        return [...rows].sort((a, b) => {
            if (matrixSort === 'label') return a.label.localeCompare(b.label);
            if (matrixSort === 'did_users') return b.did_users - a.did_users;
            return (b[matrixSort] ?? 0) - (a[matrixSort] ?? 0);
        });
    }, [patterns.action_retention_matrix, matrixSort, matrixCategory]);

    const profileRows = useMemo(() => {
        if (!data) return [] as PlaybookProfile[];
        const base = data.by_profile.filter((p) =>
            ['principiante', 'intermedio', 'experto'].includes(p.profile)
        );
        if (lens.experience === 'all') return base;
        return base.filter((p) => p.profile === lens.experience);
    }, [data, lens.experience]);

    const missingLevel = data?.by_profile.find((p) => p.profile === 'sin_dato');

    const playbooksByFocus = useMemo(() => {
        if (!data) return [] as LandingPlaybook[];
        const all = [...data.playbooks].sort((a, b) => a.priority - b.priority || a.id.localeCompare(b.id));
        const matchesLens = (pb: LandingPlaybook) => {
            if (!lensActive || lens.experience === 'all') return true;
            const hay = `${pb.id} ${pb.audience} ${pb.title}`.toLowerCase();
            return hay.includes(lens.experience);
        };
        let list: LandingPlaybook[];
        if (focus === 'summary') {
            list = data.executive_summary.length ? data.executive_summary : all.filter((p) => p.priority <= 2);
        } else if (focus === 'profiles') {
            list = all.filter((p) => p.id.startsWith('profile_'));
        } else if (focus === 'stages') {
            list = all.filter((p) => p.id.startsWith('stage_'));
        } else if (focus === 'retention' || focus === 'captures' || focus === 'pro') {
            list = all.filter((p) => p.goal === focus && !p.id.startsWith('stage_'));
        } else {
            list = all;
        }
        return list.filter(matchesLens);
    }, [data, focus, lens.experience, lensActive]);

    const tabs: { value: FocusTab; label: string }[] = [
        { value: 'summary', label: 'Resumen ejecutivo' },
        { value: 'retention', label: 'Retención' },
        { value: 'captures', label: 'Capturas' },
        { value: 'pro', label: 'Pro' },
        { value: 'profiles', label: 'Por perfil' },
        { value: 'stages', label: 'Por etapa' },
        { value: 'evidence', label: 'Evidencia' },
    ];

    if (isLoading && !data) {
        return (
            <div className="py-12 flex flex-col items-center justify-center gap-3">
                <RefreshCw size={22} className="animate-spin text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Calculando playbooks y drivers…</p>
            </div>
        );
    }

    if (isError || !data) {
        return (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-6 text-center space-y-3">
                <p className="text-sm font-semibold text-rose-400">No se pudieron cargar los playbooks</p>
                <p className="text-xs text-muted-foreground max-w-md mx-auto">
                    {(error as Error)?.message || 'Error al consultar get_early_landing_playbooks'}
                </p>
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-medium bg-primary text-primary-foreground"
                >
                    Reintentar
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Lightbulb size={16} className="text-primary" />
                        Playbooks de aterrizaje
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
                        Análisis correlacional (no causal): lift = % con acción en 48 h − % sin ella.
                        Recomendaciones accionables por objetivo, perfil y etapa de vida del usuario.
                        {lensActive && (
                            <span className="block mt-1 text-primary">
                                Lente activa: {describeLens(lens)}
                            </span>
                        )}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    title="Actualizar playbooks"
                >
                    <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
                </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <MetricCard title="Cohorte" value={data.cohort.registered} className="p-3" />
                <MetricCard title="% sesión 5" value={`${data.cohort.retained_pct}%`} className="p-3" />
                <MetricCard title="Creators" value={`${data.cohort.creator_pct}%`} className="p-3" />
                <MetricCard title="Pro" value={`${data.cohort.pro_pct}%`} className="p-3" />
                <MetricCard
                    title="Sin nivel perfil"
                    value={data.cohort.missing_experience_level}
                    className="p-3"
                />
                <MetricCard title="Playbooks" value={data.playbooks.length} className="p-3" />
            </div>

            {data.cohort.missing_experience_level > 0 && (
                <div className="flex gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
                    <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                    <p className="text-xs text-muted-foreground">
                        {data.cohort.missing_experience_level} usuarios sin experience_level
                        ({missingLevel ? `S5=${pct(missingLevel.session_5_pct)}` : 'sin_dato'}).
                        Los playbooks por perfil usan solo principiante / intermedio / experto;
                        completar el paso de nivel en onboarding mejora la personalización.
                    </p>
                </div>
            )}

            <div className="flex gap-1 p-1 bg-muted/50 rounded-lg w-fit flex-wrap">
                {tabs.map((t) => (
                    <button
                        key={t.value}
                        type="button"
                        onClick={() => setFocus(t.value)}
                        className={cn(
                            'px-3 py-1.5 rounded-md text-xs font-medium transition-all',
                            focus === t.value
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {(focus === 'summary' || focus === 'retention' || focus === 'captures' || focus === 'pro' || focus === 'profiles' || focus === 'stages') && (
                <div className="space-y-3">
                    {playbooksByFocus.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                            No hay playbooks para este filtro con la muestra actual.
                        </p>
                    ) : (
                        playbooksByFocus.map((pb) => <PlaybookCard key={pb.id} playbook={pb} />)
                    )}
                </div>
            )}

            {(focus === 'retention' || focus === 'summary') && (
                <DriversTable
                    title="Drivers de retención (→ sesión 5)"
                    subtitle="Quién hizo la acción en 48 h vs quién no"
                    rows={data.drivers.retention}
                    rateLabel="S5"
                />
            )}

            {(focus === 'captures' || focus === 'summary') && (
                <DriversTable
                    title="Drivers de capturas (→ creator ≥1)"
                    subtitle="Asociación acción temprana → publicar al menos una captura"
                    rows={data.drivers.captures}
                    rateLabel="Creator"
                />
            )}

            {(focus === 'pro' || focus === 'summary') && (
                <DriversTable
                    title="Drivers de Pro"
                    subtitle="Asociación acción temprana → tier Pro / suscripción activa"
                    rows={data.drivers.pro}
                    rateLabel="Pro"
                />
            )}

            {(focus === 'profiles' || focus === 'summary') && profileRows.length > 0 && (
                <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                    <div className="flex items-center gap-2">
                        <Users size={16} className="text-primary" />
                        <div>
                            <h3 className="text-sm font-semibold text-foreground">Por nivel de experiencia</h3>
                            <p className="text-xs text-muted-foreground">
                                Métricas del segmento + mejor palanca de retención en 48 h
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {profileRows.map((row) => (
                            <ProfileCard key={row.profile} row={row} />
                        ))}
                    </div>
                </div>
            )}

            {(focus === 'profiles' || focus === 'summary' || focus === 'retention') && (
                <MainScreensPanel rows={patterns.by_main_screen ?? []} />
            )}

            {(focus === 'stages' || focus === 'summary') && data.by_stage.length > 0 && (
                <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                    <div className="flex items-center gap-2">
                        <CalendarRange size={16} className="text-primary" />
                        <div>
                            <h3 className="text-sm font-semibold text-foreground">Por etapa de vida</h3>
                            <p className="text-xs text-muted-foreground">
                                % activos en la ventana (solo usuarios elegibles que ya alcanzaron esa edad)
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                        {[...data.by_stage]
                            .sort((a, b) => STAGE_ORDER.indexOf(a.stage as typeof STAGE_ORDER[number]) - STAGE_ORDER.indexOf(b.stage as typeof STAGE_ORDER[number]))
                            .map((st) => (
                                <div key={st.stage} className="rounded-lg border border-border/60 bg-muted/20 p-3">
                                    <p className="text-[11px] text-muted-foreground">{st.label}</p>
                                    <p className="text-lg font-semibold text-foreground mt-1">{pct(st.active_pct)}</p>
                                    <p className="text-[11px] text-muted-foreground mt-1">
                                        {st.active}/{st.eligible} activos
                                    </p>
                                    <p className="text-[11px] text-muted-foreground">
                                        Creators en etapa: {st.creators_in_stage}
                                    </p>
                                    <p className="text-[11px] text-emerald-400 mt-1">
                                        De activos → S5: {pct(st.session_5_among_active)}
                                    </p>
                                </div>
                            ))}
                    </div>
                </div>
            )}

            {(focus === 'evidence' || showEvidence) && (
                <div className="space-y-4">
                    <LegacyEvidence
                        patterns={patterns}
                        categories={categories}
                        matrixCategory={matrixCategory}
                        setMatrixCategory={setMatrixCategory}
                        matrixSort={matrixSort}
                        setMatrixSort={setMatrixSort}
                        sortedMatrix={sortedMatrix}
                        lens={lens}
                    />
                </div>
            )}

            {focus !== 'evidence' && (
                <button
                    type="button"
                    onClick={() => setShowEvidence((v) => !v)}
                    className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground"
                >
                    {showEvidence ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    {showEvidence ? 'Ocultar evidencia adicional' : 'Ver matriz y segmentos históricos'}
                </button>
            )}
        </div>
    );
}

function PlaybookCard({ playbook }: { playbook: LandingPlaybook }) {
    const style = GOAL_STYLE[playbook.goal] ?? GOAL_STYLE.retention;
    const goalLabel = PLAYBOOK_GOAL_LABELS[playbook.goal] ?? playbook.goal;

    return (
        <div className={cn('rounded-xl border p-4 space-y-3', style.border, style.bg)}>
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium', style.badge)}>
                            {style.icon}
                            {goalLabel}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                            P{playbook.priority} · {playbook.stage} · {playbook.audience}
                        </span>
                    </div>
                    <h4 className="text-sm font-semibold text-foreground">{playbook.title}</h4>
                </div>
            </div>
            <p className="text-xs text-foreground/90 leading-relaxed">{playbook.why}</p>
            <p className="text-[11px] text-muted-foreground">{playbook.evidence}</p>
            {playbook.do_now.length > 0 && (
                <div>
                    <p className="text-[11px] font-semibold text-foreground mb-1.5">Qué hacer ahora</p>
                    <ol className="list-decimal pl-4 space-y-1">
                        {playbook.do_now.map((step, i) => (
                            <li key={i} className="text-xs text-foreground/90 leading-relaxed">{step}</li>
                        ))}
                    </ol>
                </div>
            )}
            {playbook.success_metric && (
                <p className="text-[11px] text-muted-foreground border-t border-border/40 pt-2">
                    Éxito: {playbook.success_metric}
                </p>
            )}
        </div>
    );
}

function ProfileCard({ row }: { row: PlaybookProfile }) {
    const best = row.best_retention_action;
    return (
        <div className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-2">
            <p className="text-xs text-muted-foreground">
                {EXPERIENCE_LABELS[row.profile] || row.profile} · n={row.users}
            </p>
            <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                    <p className="text-[10px] text-muted-foreground">S5</p>
                    <p className="text-sm font-semibold text-emerald-400">{pct(row.session_5_pct)}</p>
                </div>
                <div>
                    <p className="text-[10px] text-muted-foreground">D7</p>
                    <p className="text-sm font-semibold">{pct(row.day_7_pct)}</p>
                </div>
                <div>
                    <p className="text-[10px] text-muted-foreground">Pro</p>
                    <p className="text-sm font-semibold text-amber-400">{pct(row.pro_pct)}</p>
                </div>
            </div>
            <div className="text-[11px] text-muted-foreground space-y-0.5">
                <p>Creators {pct(row.creator_pct)} · Lurkers {pct(row.lurker_pct)}</p>
                <p>Rincón {pct(row.rincon_48h_pct)} · Intel {pct(row.intel_48h_pct)} · Follow {pct(row.follow_48h_pct)}</p>
            </div>
            {best && liftSampleReady(best.did_n, row.users - best.did_n) && (
                <div className="rounded-md bg-background/50 border border-border/40 p-2">
                    <p className="text-[10px] text-muted-foreground">Mejor palanca retención</p>
                    <p className="text-xs font-semibold text-foreground">{best.label}</p>
                    <p className="text-[11px] text-emerald-400">
                        {formatLiftPp(best.lift, true)} pp
                        <span className="text-muted-foreground font-normal">
                            {' '}({pct(best.retained_did)} con vs {pct(best.retained_skip)} sin · n={best.did_n})
                        </span>
                    </p>
                </div>
            )}
        </div>
    );
}

function DriversTable({
    title,
    subtitle,
    rows,
    rateLabel,
}: {
    title: string;
    subtitle: string;
    rows: PlaybookDriver[];
    rateLabel: string;
}) {
    if (rows.length === 0) {
        return (
            <div className="rounded-xl border border-border bg-card p-4">
                <h3 className="text-sm font-semibold text-foreground">{title}</h3>
                <p className="text-xs text-muted-foreground mt-1">Muestra insuficiente (mín. {MIN_LIFT_GROUP} con / {MIN_LIFT_GROUP} sin).</p>
            </div>
        );
    }

    const sorted = [...rows].sort((a, b) => (b.lift ?? -999) - (a.lift ?? -999));

    return (
        <div className="rounded-xl border border-border bg-card p-4 overflow-x-auto">
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            <p className="text-xs text-muted-foreground mb-3">{subtitle}</p>
            <table className="w-full text-xs min-w-[720px]">
                <thead>
                    <tr className="text-muted-foreground border-b border-border">
                        <th className="text-left py-2 font-medium">Acción (48 h)</th>
                        <th className="text-right py-2 font-medium">Con</th>
                        <th className="text-right py-2 font-medium">Sin</th>
                        <th className="text-right py-2 font-medium">{rateLabel} con</th>
                        <th className="text-right py-2 font-medium">{rateLabel} sin</th>
                        <th className="text-right py-2 font-medium">Lift</th>
                        <th className="text-right py-2 font-medium">Lift D7</th>
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((row) => {
                        const ready = liftSampleReady(row.did_n, row.skip_n);
                        const liftLabel = formatLiftPp(row.lift, ready);
                        const liftD7Label = formatLiftPp(row.lift_d7, ready);
                        return (
                        <tr key={row.key} className="border-b border-border/40 hover:bg-muted/20">
                            <td className="py-2 font-medium text-foreground">{row.label}</td>
                            <td className="py-2 text-right">{row.did_n}</td>
                            <td className="py-2 text-right">{row.skip_n}</td>
                            <td className="py-2 text-right text-emerald-400">{ready ? pct(row.rate_did) : '—'}</td>
                            <td className="py-2 text-right text-rose-400/80">{ready ? pct(row.rate_skip) : '—'}</td>
                            <td className={cn(
                                'py-2 text-right font-semibold',
                                !ready ? 'text-muted-foreground' : (row.lift ?? 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'
                            )}>
                                <span className="inline-flex items-center gap-1 justify-end">
                                    {ready && ((row.lift ?? 0) >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />)}
                                    {liftLabel}
                                </span>
                            </td>
                            <td className={cn(
                                'py-2 text-right font-semibold',
                                !ready ? 'text-muted-foreground' : (row.lift_d7 ?? 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'
                            )}>
                                {liftD7Label}
                            </td>
                        </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function LegacyEvidence({
    patterns,
    categories,
    matrixCategory,
    setMatrixCategory,
    matrixSort,
    setMatrixSort,
    sortedMatrix,
    lens,
}: {
    patterns: LandingPatterns;
    categories: string[];
    matrixCategory: string;
    setMatrixCategory: (v: string) => void;
    matrixSort: MatrixSort;
    setMatrixSort: (v: MatrixSort) => void;
    sortedMatrix: ActionRetentionRow[];
    lens: ProfileLens;
}) {
    const winners = patterns.profile_x_action_winners.filter((w) => {
        if (w.profile_value === 'sin_dato') return false;
        if (w.did_users < MIN_LIFT_GROUP || w.lift == null) return false;
        if (lens.experience !== 'all' && w.profile_value !== lens.experience) return false;
        return true;
    });
    const waterRows = patterns.by_water_type.filter(
        (r) => lens.water === 'all' || r.segment === lens.water
    );
    const opportunities = patterns.opportunities.filter(
        (op) => !/sin_dato/i.test(`${op.title} ${op.body} ${op.audience}`)
    );

    return (
        <div className="space-y-4">
            {opportunities.length > 0 && (
                <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Target size={16} className="text-primary" />
                        Señales adicionales
                    </h3>
                    {opportunities.map((op, i) => (
                        <div
                            key={i}
                            className={cn(
                                'flex gap-3 rounded-xl border p-4',
                                op.severity === 'warning'
                                    ? 'border-amber-500/30 bg-amber-500/5'
                                    : 'border-primary/30 bg-primary/5'
                            )}
                        >
                            {op.severity === 'warning'
                                ? <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                                : <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />}
                            <div className="min-w-0">
                                <p className="text-sm font-semibold text-foreground">{op.title}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{op.body}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {winners.length > 0 && (
                <div className="rounded-xl border border-border bg-card p-4">
                    <h3 className="text-sm font-semibold text-foreground mb-3">
                        Mejor acción por perfil (matriz histórica)
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {winners.map((w) => (
                            <div
                                key={`${w.profile_value}-${w.best_action}`}
                                className="rounded-lg border border-border/60 bg-muted/20 p-3"
                            >
                                <p className="text-xs text-muted-foreground">
                                    {EXPERIENCE_LABELS[w.profile_value] || w.profile_value}
                                </p>
                                <p className="text-sm font-semibold text-foreground mt-0.5">
                                    {w.best_action_label}
                                </p>
                                <p className="text-xs text-emerald-400 font-medium mt-1">
                                    {formatLiftPp(w.lift, true)} pp a sesión 5 · n={w.did_users}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <SegmentTable
                    title="Por tipo de agua"
                    rows={waterRows.map((r) => ({
                        key: r.segment,
                        label: WATER_LABELS[r.segment] || r.segment,
                        users: r.users,
                        s5: r.session_5_pct,
                        d7: r.day_7_pct,
                        extra: `${r.creator_pct}% creators`,
                    }))}
                />
                <SegmentTable
                    title="Por comportamiento"
                    rows={patterns.by_behavior.map((r) => ({
                        key: r.segment,
                        label: BEHAVIOR_LABELS[r.segment] || r.segment,
                        users: r.users,
                        s5: r.session_5_pct,
                        d7: r.day_7_pct,
                        extra: r.definition,
                    }))}
                />
            </div>

            {patterns.by_modality.length > 0 && (
                <SegmentTable
                    title="Por modalidad"
                    rows={patterns.by_modality
                        .filter((r) => lens.modality === 'all' || r.modality === lens.modality)
                        .map((r) => ({
                            key: r.modality,
                            label: r.modality,
                            users: r.users,
                            s5: r.session_5_pct,
                            d7: r.day_7_pct,
                            extra: `${r.creator_pct}% creators`,
                        }))}
                />
            )}

            <div className="rounded-xl border border-border bg-card p-4 overflow-x-auto">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div>
                        <h3 className="text-sm font-semibold text-foreground">Matriz acción × retención</h3>
                        <p className="text-xs text-muted-foreground">Evidencia completa del RPC de analytics</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <select
                            value={matrixCategory}
                            onChange={(e) => setMatrixCategory(e.target.value)}
                            className="bg-muted/50 border border-border rounded-md text-xs px-2 py-1.5 text-foreground"
                        >
                            {categories.map((c) => (
                                <option key={c} value={c}>
                                    {c === 'all' ? 'Todas las categorías' : (CATEGORY_LABELS[c] || c)}
                                </option>
                            ))}
                        </select>
                        <select
                            value={matrixSort}
                            onChange={(e) => setMatrixSort(e.target.value as MatrixSort)}
                            className="bg-muted/50 border border-border rounded-md text-xs px-2 py-1.5 text-foreground"
                        >
                            <option value="lift_session_5">Orden: lift sesión 5</option>
                            <option value="lift_day_7">Orden: lift día 7</option>
                            <option value="did_users">Orden: volumen</option>
                            <option value="label">Orden: nombre</option>
                        </select>
                    </div>
                </div>
                <MatrixTable rows={sortedMatrix} />
            </div>
        </div>
    );
}

function SegmentTable({
    title,
    rows,
}: {
    title: string;
    rows: { key: string; label: string; users: number; s5: number; d7: number; extra?: string }[];
}) {
    return (
        <div className="rounded-xl border border-border bg-card p-4 overflow-x-auto">
            <h3 className="text-sm font-semibold text-foreground mb-3">{title}</h3>
            <table className="w-full text-xs">
                <thead>
                    <tr className="text-muted-foreground border-b border-border">
                        <th className="text-left py-2 font-medium">Segmento</th>
                        <th className="text-right py-2 font-medium">Users</th>
                        <th className="text-right py-2 font-medium">S5%</th>
                        <th className="text-right py-2 font-medium">D7%</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.key} className="border-b border-border/40">
                            <td className="py-2">
                                <p className="font-medium text-foreground">{row.label}</p>
                                {row.extra && (
                                    <p className="text-[10px] text-muted-foreground">{row.extra}</p>
                                )}
                            </td>
                            <td className="py-2 text-right align-top">{row.users}</td>
                            <td className="py-2 text-right align-top text-emerald-400 font-semibold">{row.s5}%</td>
                            <td className="py-2 text-right align-top">{row.d7}%</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function MatrixTable({ rows }: { rows: ActionRetentionRow[] }) {
    if (rows.length === 0) {
        return <p className="text-xs text-muted-foreground">Sin filas para comparar.</p>;
    }

    return (
        <table className="w-full text-xs min-w-[800px]">
            <thead>
                <tr className="text-muted-foreground border-b border-border">
                    <th className="text-left py-2 font-medium">Acción</th>
                    <th className="text-left py-2 font-medium">Cat.</th>
                    <th className="text-right py-2 font-medium">Con</th>
                    <th className="text-right py-2 font-medium">Sin</th>
                    <th className="text-right py-2 font-medium">S5 con</th>
                    <th className="text-right py-2 font-medium">S5 sin</th>
                    <th className="text-right py-2 font-medium">Lift S5</th>
                    <th className="text-right py-2 font-medium">Lift D7</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => {
                    const ready = liftSampleReady(row.did_users, row.skip_users);
                    return (
                    <tr key={row.key} className="border-b border-border/40 hover:bg-muted/20">
                        <td className="py-2 text-foreground font-medium max-w-[180px]">{row.label}</td>
                        <td className="py-2 text-muted-foreground">
                            {CATEGORY_LABELS[row.category] || row.category}
                        </td>
                        <td className="py-2 text-right">{row.did_users}</td>
                        <td className="py-2 text-right">{row.skip_users}</td>
                        <td className="py-2 text-right text-emerald-400">{ready ? `${row.did_session_5_pct}%` : '—'}</td>
                        <td className="py-2 text-right text-rose-400/80">{ready ? `${row.skip_session_5_pct}%` : '—'}</td>
                        <td className={cn(
                            'py-2 text-right font-semibold',
                            !ready ? 'text-muted-foreground' : row.lift_session_5 >= 0 ? 'text-emerald-400' : 'text-rose-400'
                        )}>
                            {formatLiftPp(row.lift_session_5, ready)}
                        </td>
                        <td className={cn(
                            'py-2 text-right font-semibold',
                            !ready ? 'text-muted-foreground' : row.lift_day_7 >= 0 ? 'text-emerald-400' : 'text-rose-400'
                        )}>
                            {formatLiftPp(row.lift_day_7, ready)}
                        </td>
                    </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

// Keep type import used for clarity in goal filters